import { createApp, reactive } from "vue";
import App from "./App.vue";
import router from "./router";
import vuetify from "./plugins/vuetify";
import { loadFonts } from "./plugins/webfontloader";
import { translations } from "./translations";

loadFonts();

const envFlag = (value, defaultValue = false) => {
  if (value == null || value === "") return defaultValue;
  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};

const isProdBuild = process.env.NODE_ENV === "production";
const PREMIUM_ENABLED = envFlag(process.env.VUE_APP_PREMIUM_ENABLED, !isProdBuild);
const ADVANCED_ENABLED = envFlag(process.env.VUE_APP_ADVANCED_ENABLED, !isProdBuild);
const ANALYTICS_ENABLED = envFlag(process.env.VUE_APP_ENABLE_ANALYTICS, false);
const GA_MEASUREMENT_ID = (process.env.VUE_APP_GA_MEASUREMENT_ID || "").trim();
const MOCK_AUTH_MODE = (process.env.VUE_APP_MOCK_AUTH || "").trim().toLowerCase();
const MOCK_USER_EMAIL = (process.env.VUE_APP_MOCK_USER_EMAIL || "dev@onlygrades.local").trim();
const MOCK_USER_NAME = (process.env.VUE_APP_MOCK_USER_NAME || "Dev User").trim();
const MOCK_IS_PAID = envFlag(process.env.VUE_APP_MOCK_IS_PAID, false);
const DEV_AUTH_CONTROLS_ENABLED = envFlag(
  process.env.VUE_APP_DEV_AUTH_CONTROLS,
  !isProdBuild
);
const ANALYTICS_CONSENT_KEY = "onlygrades_analytics_consent";
const ANALYTICS_CONSENT_ACCEPTED = "accepted";
const ANALYTICS_CONSENT_REJECTED = "rejected";
const ANALYTICS_CONSENT_EVENT = "onlygrades:analytics-consent-changed";

let analyticsInitialized = false;

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed loading script: ${src}`));
    document.head.appendChild(script);
  });

const initAnalytics = async () => {
  if (analyticsInitialized) return;
  if (!ANALYTICS_ENABLED || !GA_MEASUREMENT_ID) return;
  try {
    await loadScript(
      `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    );
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID);
    analyticsInitialized = true;
  } catch (error) {
    console.warn("Analytics initialization failed", error);
  }
};

const readAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (
      raw === ANALYTICS_CONSENT_ACCEPTED ||
      raw === ANALYTICS_CONSENT_REJECTED
    ) {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
};

const setAnalyticsDisabledFlag = (disabled) => {
  if (!GA_MEASUREMENT_ID) return;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = Boolean(disabled);
};

const syncAnalyticsConsent = () => {
  const consent = readAnalyticsConsent();
  const hasConsent = consent === ANALYTICS_CONSENT_ACCEPTED;
  setAnalyticsDisabledFlag(!hasConsent);
  if (hasConsent) {
    void initAnalytics();
  }
};

syncAnalyticsConsent();
window.addEventListener(ANALYTICS_CONSENT_EVENT, syncAnalyticsConsent);

const TOKEN_STORAGE_KEY = "onlygrades_auth_token";
const USER_STORAGE_KEY = "onlygrades_user";
const ENTITLEMENT_STORAGE_KEY = "onlygrades_entitlement";

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    void 0;
  }
};

const buildMockEntitlement = (isPaid) =>
  isPaid
    ? { plan: "premium", status: "paid", is_paid: true, is_mock: true }
    : { plan: "free", status: "free", is_paid: false, is_mock: true };

// Create a reactive state for the language
const state = reactive({
  locale: "no", // Set Norwegian as the default language
  translations,
  authToken: localStorage.getItem(TOKEN_STORAGE_KEY) || "",
  user: readJson(USER_STORAGE_KEY, null),
  entitlement: readJson(ENTITLEMENT_STORAGE_KEY, {
    plan: "free",
    status: "free",
    is_paid: false,
  }),
  authStorageKeys: {
    token: TOKEN_STORAGE_KEY,
    user: USER_STORAGE_KEY,
    entitlement: ENTITLEMENT_STORAGE_KEY,
  },
  authMockConfig: {
    controlsEnabled: DEV_AUTH_CONTROLS_ENABLED,
    mode: MOCK_AUTH_MODE,
    defaultEmail: MOCK_USER_EMAIL || "dev@onlygrades.local",
    defaultName: MOCK_USER_NAME || "Dev User",
  },
  features: {
    premiumEnabled: PREMIUM_ENABLED,
    advancedEnabled: ADVANCED_ENABLED,
    analyticsEnabled: ANALYTICS_ENABLED && Boolean(GA_MEASUREMENT_ID),
  },
  analyticsConsent: readAnalyticsConsent(),
  analyticsConsentConfig: {
    key: ANALYTICS_CONSENT_KEY,
    accepted: ANALYTICS_CONSENT_ACCEPTED,
    rejected: ANALYTICS_CONSENT_REJECTED,
    eventName: ANALYTICS_CONSENT_EVENT,
  },
});

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(ENTITLEMENT_STORAGE_KEY);
};

if (!PREMIUM_ENABLED) {
  state.authToken = "";
  state.user = null;
  state.entitlement = { plan: "free", status: "free", is_paid: false };
  clearStoredAuth();
}

const applyMockAuth = () => {
  if (!PREMIUM_ENABLED) return;
  if (!MOCK_AUTH_MODE) return;
  const normalizedMode = MOCK_AUTH_MODE.replace(/[-_\s]/g, "");
  if (normalizedMode === "loggedout" || normalizedMode === "logout") {
    state.authToken = "";
    state.user = null;
    state.entitlement = { plan: "free", status: "free", is_paid: false };
    clearStoredAuth();
    return;
  }
  if (normalizedMode === "loggedin" || normalizedMode === "login") {
    const entitlement = buildMockEntitlement(MOCK_IS_PAID);
    state.authToken = "";
    state.user = {
      email: MOCK_USER_EMAIL,
      name: MOCK_USER_NAME,
      picture_url: null,
      is_mock_user: true,
    };
    state.entitlement = entitlement;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    writeJson(USER_STORAGE_KEY, state.user);
    writeJson(ENTITLEMENT_STORAGE_KEY, state.entitlement);
  }
};

applyMockAuth();

const app = createApp(App);

// Provide the state to the app
app.provide("state", state);

app.use(vuetify).use(router).mount("#app");
